import { CAMINO } from '@somnhold/content';
import type { LitterRules } from '../types.ts';
import type { ChapelLayout, BendsLayout, StreetLayout } from './litter-layout.ts';

/**
 * The stretcher's rules from its layouts (docs/sendas/clerigo.md, chapter 1; content CAMINO): the fight's builder
 * (campaign/sendas.ts) and the renderer's scenes build them the same way. Life in hit points worked out by the caller
 * (seconds of D): the limpets' and the beam's burn.
 */

export const base = (): Omit<LitterRules, 'kind' | 'way' | 'dens' | 'foes' | 'crows' | 'limpet' | 'mirrors'> => {
  const L = CAMINO.litter;
  return { near: L.near, pace: L.pace, lift: L.lift, liftReach: L.liftReach, liftBp: L.liftBp, mendReach: L.mendReach, mendBp: L.mendBp, light: L.light, blow: { ...L.blow }, hold: L.hold, ridge: null, walk: null, span: null, snow: null };
};

export const streetRules = (L: StreetLayout): LitterRules => {
  const S = CAMINO.street;
  return {
    ...base(),
    kind: 'street',
    way: L.way.map((p) => ({ ...p })),
    dens: L.dens.map((d) => ({ ...d })),
    foes: { first: S.stalker.first, gap: [S.stalker.gap[0], S.stalker.gap[1]], n: [S.stalker.n[0], S.stalker.n[1]], ahead: S.stalker.ahead, every: 0 },
    crows: { ...S.crows, at: 'hero' },
    limpet: null,
    mirrors: null,
  };
};

export const chapelRules = (L: ChapelLayout, burn: number): LitterRules => {
  const C = CAMINO.chapel;
  // The stalkers come up the terraces from the two bottom corners.
  const R = L.radius;
  const dens = [
    { x: -R * 1000 + 1500, y: (R - 2) * 866, at: 0 },
    { x: R * 1000 - 1500, y: (R - 2) * 866, at: 0 },
  ];
  return {
    ...base(),
    kind: 'chapel',
    way: [{ ...L.rest }],
    dens,
    foes: { first: C.stalker.first, gap: [C.stalker.every, C.stalker.every], n: [1, 1], ahead: 0, every: C.stalker.every },
    crows: null,
    limpet: null,
    mirrors: {
      hooks: L.hooks.map((h, i) => ({ q: h.q, r: h.r, dir: h.dir, stand: { ...(L.hookStand[i] as { x: number; y: number }) } })),
      mirrors: L.mirrors.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.mirrorStand[i] as { x: number; y: number }) } })),
      lamp: { ...L.lamp },
      walls: L.walls.map((w) => ({ ...w })),
      radius: L.radius,
      start: [...L.start0],
      stand: C.stand,
      reach: C.reach,
      burn,
      open: C.open,
    },
  };
};

export const bendsRules = (L: BendsLayout, limpetHp: number): LitterRules => {
  const S = CAMINO.slope;
  const K = S.limpet;
  return {
    ...base(),
    kind: 'slope',
    way: L.way.map((p) => ({ ...p })),
    dens: L.dens.map((d) => ({ ...d })),
    foes: null,
    crows: { ...S.crows, at: 'carriers' },
    limpet: { hp: limpetHp, speed: K.speed, drainBp: K.drainBp, heroBp: K.heroBp, burst: K.burst, near: K.near, trickle: K.trickle, cap: K.cap, leap: K.leap, bends: L.bends.map((b) => ({ ...b })) },
    mirrors: null,
  };
};
